import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Producto } from '@tanteo/compartido';
import { formatearCOP } from '@tanteo/compartido';

/** Acceso táctil por categoría para productos sin código a la mano (02-ux §3.1). */
export function BotonesRapidos({ productos, onElegir }: { productos: Producto[]; onElegir: (p: Producto) => void }) {
  const categorias = useMemo(() => [...new Set(productos.filter((p) => p.activo).map((p) => p.categoria))], [productos]);
  const [categoria, setCategoria] = useState<string | null>(null);
  const rejilla = useRef<HTMLDivElement>(null);
  const [hayMas, setHayMas] = useState(false);

  /**
   * Si queda contenido por debajo del borde. El navegador puede no pintar ninguna barra, así que el
   * bloque mide su propio desborde y lo dice desvaneciendo el borde.
   */
  const mirarSiHayMas = useCallback(() => {
    const e = rejilla.current;
    if (e) setHayMas(e.scrollHeight - e.scrollTop - e.clientHeight > 4);
  }, []);

  useEffect(() => {
    mirarSiHayMas();
    window.addEventListener('resize', mirarSiHayMas);
    return () => window.removeEventListener('resize', mirarSiHayMas);
    // La lista cambia al entrar y salir de una categoría, así que se vuelve a medir con ella.
  }, [mirarSiHayMas, categoria, productos]);

  if (categorias.length === 0) return null;

  return (
    <aside className="w-full lg:w-1/3 shrink-0">
      {/* Las columnas se ajustan al nombre y no al revés: con tres fijas, un nombre como
          «04_Salpicones» se salía del botón. Y con tope, porque el atajo no debe ser más alto que la
          venta que está al lado — con la barra a la vista, para que se sepa que hay más abajo. */}
      <div
        ref={rejilla}
        onScroll={mirarSiHayMas}
        className={`scroll-visible grid gap-2 grid-cols-[repeat(auto-fit,minmax(7.5rem,1fr))] max-h-[24rem] overflow-y-auto ${
          hayMas ? 'hay-mas-abajo' : ''
        }`}
        data-e2e="categorias-scroll"
      >
        {categoria === null
          ? categorias.map((c) => (
              <button
                key={c}
                onClick={() => setCategoria(c)}
                className="rounded-card bg-surface hover:bg-primary/15 min-h-12 break-words px-3 py-2 text-contenido font-bold"
              >
                {c}
              </button>
            ))
          : [
              <button
                key="_volver"
                onClick={() => setCategoria(null)}
                className="rounded-card border-border col-span-full w-full border px-3 py-2 text-contenido font-bold"
              >
                ← Categorías
              </button>,
              ...productos
                .filter((p) => p.activo && p.categoria === categoria)
                .map((p) => (
                  <button
                    key={p.id}
                    onClick={() => onElegir(p)}
                    className="rounded-card bg-surface hover:bg-primary/15 min-h-16 break-words px-3 py-2 text-left"
                  >
                    <span className="block text-contenido font-bold leading-tight">{p.nombre}</span>
                    <span className="num text-muted text-etiqueta">{formatearCOP(p.precio)}</span>
                  </button>
                )),
            ]}
      </div>
    </aside>
  );
}
